import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Frontend fallback — used only if the CMS call fails. Every question here
// is also editable from the Admin → Event Page → FAQ tab.
const FALLBACK_FAQS = [
  { question: 'When is the RSVP deadline?', answer: 'Please RSVP by May 30, 2027. This will help us finalize catering and seating arrangements.' },
];

const FAQ = () => {
  const [faqs, setFaqs] = useState(FALLBACK_FAQS);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    let alive = true;
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${API}/event/content`, { params: { _: Date.now() } });
        const cmsFaqs = res.data?.faqs;
        if (alive && Array.isArray(cmsFaqs) && cmsFaqs.length) {
          setFaqs(cmsFaqs);
        }
      } catch { /* keep fallback */ }
    };
    fetchContent();
    const onVis = () => { if (document.visibilityState === 'visible') fetchContent(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; document.removeEventListener('visibilitychange', onVis); };
  }, []);

  return (
    <section id="faq" className="section bg-ivory" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-cursive text-[#C8A951] text-2xl mb-2">Questions?</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#3B2F2F] tracking-tight">
            Frequently Asked
          </h2>
          <div className="gold-divider" />
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => {
            // The Admin CMS stores each FAQ as `{ q, a }` (matching the
            // editor UI) — earlier revisions of this component read
            // `question` / `answer` instead, which is why the shipped
            // accordion titles rendered blank against a clean CMS payload.
            // Accept either shape so the page keeps working no matter
            // which key set a record was authored with.
            const question = faq.question ?? faq.q ?? '';
            const answer   = faq.answer   ?? faq.a ?? '';
            return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="wedding-card overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between text-left p-0"
                data-testid={`faq-question-${index}`}
              >
                <div className="flex items-center gap-3">
                  <HelpCircle className="w-5 h-5 text-[#C8A951] flex-shrink-0" />
                  <span className="font-serif text-lg text-[#3B2F2F]">{question}</span>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-[#C8A951] transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="text-[#5A4A4A] pt-4 pl-8 leading-relaxed" data-testid={`faq-answer-${index}`}>
                      {answer}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export { FAQ };
